import { __assign } from "tslib";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import styles from "./styles.css.js";
import InternalIcon from "../../icon/internal";
import ItemsList from "../items-list";
import Dropdown from "../../internal/components/dropdown";
import useHiddenDescription from "../utils/use-hidden-description";
import Tooltip from "../tooltip.js";
import { getMenuItemProps } from "../utils/menu-item";
var ExpandableCategoryElement = function(_a) {
    var _b, _c;
    var item = _a.item, onItemActivate = _a.onItemActivate, onGroupToggle = _a.onGroupToggle, targetItem = _a.targetItem, isHighlighted = _a.isHighlighted, isKeyboardHighlight = _a.isKeyboardHighlight, isExpanded = _a.isExpanded, highlightItem = _a.highlightItem, disabled = _a.disabled, expandToViewport = _a.expandToViewport, variant = _a.variant;
    var highlighted = isHighlighted(item);
    var expanded = isExpanded(item);
    var isKeyboardHighlighted = isKeyboardHighlight(item);
    var triggerRef = React.useRef(null);
    var ref = useRef(null);
    useEffect(function() {
        if (triggerRef.current && highlighted && !expanded) {
            triggerRef.current.focus();
        }
    }, [
        expanded,
        highlighted
    ]);
    var onClick = function(event) {
        var _a;
        if (!disabled) {
            event.preventDefault();
            onGroupToggle(item, event);
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    var onHover = function(event) {
        event.preventDefault();
        highlightItem(item);
    };
    var isDisabledWithReason = !!item.disabledReason && item.disabled;
    var _d = useHiddenDescription(item.disabledReason), targetProps = _d.targetProps, descriptionEl = _d.descriptionEl;
    var trigger = item.text && /*#__PURE__*/ React.createElement("span", __assign({
        className: clsx(styles.header, styles["expandable-header"], styles["variant-".concat(variant)], (_b = {}, _b[styles.disabled] = disabled, _b[styles.highlighted] = highlighted, _b[styles["is-focused"]] = isKeyboardHighlighted, _b)),
        // We are using the roving tabindex technique to manage the focus state of the dropdown.
        // The current element will always have tabindex=0 which means that it can be tabbed to,
        // while all other items have tabindex=-1 so we can focus them when necessary.
        tabIndex: highlighted ? 0 : -1,
        ref: triggerRef
    }, getMenuItemProps({
        parent: true,
        expanded: expanded,
        disabled: disabled
    }), isDisabledWithReason ? targetProps : {}), item.text, /*#__PURE__*/ React.createElement("span", {
        className: clsx(styles["expand-icon"], styles["expand-icon-right"])
    }, /*#__PURE__*/ React.createElement(InternalIcon, {
        name: "caret-down-filled"
    })));
    var content;
    // If the category element is disabled, we do not render a dropdown.
    // Screenreaders are confused by additional sections
    if (isDisabledWithReason) {
        content = /*#__PURE__*/ React.createElement(Tooltip, {
            content: item.disabledReason
        }, trigger, descriptionEl);
    } else if (disabled) {
        content = trigger;
    } else {
        content = /*#__PURE__*/ React.createElement(Dropdown, {
            open: expanded,
            stretchWidth: false,
            interior: true,
            expandToViewport: expandToViewport,
            trigger: trigger
        }, item.items && expanded && /*#__PURE__*/ React.createElement("ul", {
            role: "menu",
            "aria-label": item.text,
            className: clsx(styles["items-list-container"])
        }, /*#__PURE__*/ React.createElement(ItemsList, {
            items: item.items,
            onItemActivate: onItemActivate,
            onGroupToggle: onGroupToggle,
            targetItem: targetItem,
            isHighlighted: isHighlighted,
            isKeyboardHighlight: isKeyboardHighlight,
            isExpanded: isExpanded,
            highlightItem: highlightItem,
            variant: variant
        })));
    }
    return /*#__PURE__*/ React.createElement("li", {
        className: clsx(styles.category, styles["variant-".concat(variant)], styles.expandable, (_c = {}, _c[styles.expanded] = expanded, _c[styles.disabled] = disabled, _c[styles.highlighted] = highlighted, _c)),
        role: "presentation",
        "data-testid": item.id,
        ref: ref,
        onClick: onClick,
        onMouseEnter: onHover,
        onTouchStart: onHover
    }, content);
};
export default ExpandableCategoryElement; //# sourceMappingURL=expandable-category-element.js.map
